<script setup lang="ts">
import { computed } from 'vue'

import mdiAlertOctagonOutline from '~icons/mdi/alert-octagon-outline'
import mdiCheckboxMarkedCircleOutline from '~icons/mdi/checkbox-marked-circle-outline'
import mdiDotsHorizontalCircleOutline from '~icons/mdi/dots-horizontal-circle-outline'

import type { FeedbackState } from './types'

// Feedback window shared by the S*Dialog flows (PR #895 review): centered state icon, bold
// title, message body (default slot) and a stretched return button. Purely presentational —
// the consumer owns all wording.
//
// `pending` was added for the payment dialog (ADA-4432 D13) — see ./types.
const { feedback } = defineProps<{
  feedback: FeedbackState | null
  title: string
  buttonText: string
}>()

const emit = defineEmits<{ close: [] }>()

const icon = computed(() => {
  if (feedback === 'success') return mdiCheckboxMarkedCircleOutline
  return feedback === 'pending' ? mdiDotsHorizontalCircleOutline : mdiAlertOctagonOutline
})
const color = computed(() => {
  if (feedback === 'success') return 'success'
  // Legacy used its `neutral` colour here; the Vuetify 4 theme expresses the same as a
  // de-emphasised default rather than a semantic state colour.
  return feedback === 'pending' ? 'medium-emphasis' : 'error'
})
</script>

<template>
  <v-sheet class="d-flex flex-column align-center">
    <v-icon
      :color="color"
      :icon="icon"
      size="72"
      class="my-8"
    />

    <div class="text-headline-large font-weight-bold text-center px-4">{{ title }}</div>

    <div style="max-width: 400px">
      <div class="text-body-large pa-4">
        <slot />
      </div>
    </div>

    <v-btn
      variant="outlined"
      class="align-self-stretch mx-4 mb-12 mt-4"
      @click="emit('close')"
    >
      {{ buttonText }}
    </v-btn>
  </v-sheet>
</template>
